// packages/engine/src/process-lanes.ts
//
// BPMN lane-plan evaluation (plan 2026-07-07-bpmn-lanes-pools P2). Turns a
// `flow … bpmn { lane …; pool … }` body into an ordered lane/pool plan over
// the rendered walk's performers:
//
//   - Claims are FIRST-WINS in clause document order; within one plural /
//     `by` clause, lanes order lexicographically by title (qualified-path
//     tie-break — spec §8.1 needs a total order).
//   - Persona actors (owners absent from the model — note steps, control
//     prefixes) are not in the selector universe; a bare ref or an or-union
//     of refs additionally claims them by single-segment name equality.
//     Aspect atoms, `by` getters, and pool queries skip actors — an actor
//     carries no aspects, so aspect-shaped lanes never contain personas and
//     a lane containing one never satisfies a pool query (fail-closed).
//   - The default lane is the implicit trailing catch-all (title = null);
//     the post-pass decides whether its band renders (shown iff non-empty).
//   - A lane joins the FIRST pool (declaration order) whose query EVERY
//     member performer matches; `pool by` pools a lane iff all members share
//     the value. Pools with no lanes are dropped.
//   - Render order: pooled lanes group contiguously at their pool's anchor
//     (the pool's first member's declaration position), everything else
//     keeps declaration order; the default lane declares last.
//
// Owner-path canonicalization: `BpmnOwner.path` may be a raw written path
// when resolution failed (process-bpmn.ts ownerOf) — bridge exact qualified
// key → unique suffix → unbridged. This deliberately EXTENDS the live color
// recipe (ownerColorFor: exact → unique suffix, no raw fallback): an
// ambiguous suffix matches nothing and the performer stays in the default
// lane.

import type { SourceSpan } from "./types.js";
import type { QualifiedName, Value } from "./types.js";
import type { FlowBodyClause } from "./v010-ast.js";
import type { Selector, ExprGetter } from "./selector-ast.js";
import { exprText } from "./selector-ast.js";
import type { EvalContext, ElementNode } from "./selector-eval.js";
import { evalSelector } from "./selector-eval.js";
import type {
  BpmnLaneBand,
  BpmnLayout,
  BpmnNode,
  BpmnOwner,
  BpmnPoolBand,
} from "./process-bpmn-types.js";

// =============================================================================
// Performers
// =============================================================================

/** A distinct performer of the rendered layout — the lane-membership unit. */
export interface BpmnPerformer {
  readonly path: QualifiedName;
  readonly instance?: string;
  readonly isActor: boolean;
  readonly label: string;
}

/** Membership key — path + instance (`Payments#eu`), the §2 identity rule. */
export function performerKey(p: {
  readonly path: QualifiedName;
  readonly instance?: string;
}): string {
  return p.path.join(".") + (p.instance !== undefined ? `#${p.instance}` : "");
}

/**
 * The rendered layout's distinct performers, document order (= node order).
 * Compensation nodes are excluded — comp bands stay global, outside the lane
 * model — and ownerless nodes contribute nothing (TODO steps and furniture
 * resolve in the post-pass, not here).
 */
export function performersOf(layout: BpmnLayout): BpmnPerformer[] {
  const seen = new Set<string>();
  const out: BpmnPerformer[] = [];
  for (const n of layout.nodes) {
    if ("compensation" in n && n.compensation) continue;
    const owner: BpmnOwner | undefined = "owner" in n ? n.owner : undefined;
    if (!owner || owner.path.length === 0) continue;
    const key = performerKey(owner);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      path: owner.path,
      ...(owner.instance !== undefined ? { instance: owner.instance } : {}),
      isActor: owner.isActor,
      label: owner.label,
    });
  }
  return out;
}

// =============================================================================
// Plan shapes
// =============================================================================

export interface BpmnLaneDef {
  /** Stable id: element path | `title:<string>` | `val:<canonical>` | `default`. */
  readonly id: string;
  /** Display title; null = the untitled default lane (no label chip). */
  readonly title: string | null;
  /** Member performer keys (path#instance), claim order. */
  readonly memberKeys: readonly string[];
  readonly poolId?: string;
  /** WYSIWYG edit correlation — see {@link BpmnLaneBand}. */
  readonly sourceClauseIndex?: number;
  readonly editable?: boolean;
  readonly ownerModule?: string;
}

export interface BpmnPoolDef {
  readonly id: string;
  readonly title: string;
  readonly sourceClauseIndex?: number;
  readonly editable?: boolean;
}

export interface BpmnLaneLint {
  readonly kind: "empty-lane" | "multi-value" | "pools-only" | "bad-selector";
  readonly message: string;
  readonly source?: SourceSpan;
}

/** The default-lane sentinel id. `#` cannot appear in an element path, so a
 *  module literally named `default` can never collide with it. */
export const DEFAULT_LANE_ID = "#default";

export interface BpmnLanePlan {
  /** Render order (pool-contiguous). Includes the default lane (last unless pooled). */
  readonly lanes: readonly BpmnLaneDef[];
  readonly pools: readonly BpmnPoolDef[];
  readonly lints: readonly BpmnLaneLint[];
}

// =============================================================================
// Canonicalization + value display
// =============================================================================

/** exact key → unique suffix → undefined (ambiguous/unknown stays unbridged). */
function universeNodeFor(
  ctx: EvalContext,
  path: QualifiedName,
): ElementNode | undefined {
  const key = path.join(".");
  const exact = ctx.universe.byKey(key);
  if (exact) return exact;
  const suffix = "." + key;
  let found: ElementNode | undefined;
  for (const n of ctx.universe.nodes) {
    if (n.key.endsWith(suffix)) {
      if (found) return undefined; // ambiguous — fail closed
      found = n;
    }
  }
  return found;
}

/** Canonical string for a `by` value / `val:` lane id (ref values → the
 *  referenced element's qualified key when it resolves; else the raw text). */
function canonicalValue(v: Value, ctx: EvalContext): string {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "ref": {
      const n = universeNodeFor(ctx, v.path);
      return n ? n.key : v.path.join(".");
    }
    case "number":
    case "boolean":
      return String(v.value);
    case "list":
      // Aspect lists are flattened by the universe builder; a nested list
      // canonicalizes element-wise (defensive — not producible from source).
      return v.items.map((i) => canonicalValue(i, ctx)).join(",");
    case "expr":
      return exprText(v.expr);
  }
}

/** Display title for a `by` value — ref values show the ref's name. */
function displayValue(v: Value): string {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "ref":
      return v.path[v.path.length - 1] ?? "";
    case "number":
    case "boolean":
      return String(v.value);
    case "list":
      return v.items.map(displayValue).join(", ");
    case "expr":
      return exprText(v.expr);
  }
}

/** The ref atoms of a bare ref / or-union of refs; undefined for any other
 *  shape (the §2 gate for actor-by-name matching). */
function refAtomsOf(sel: Selector): readonly QualifiedName[] | undefined {
  if (sel.kind === "ref") return [sel.path];
  if (sel.kind === "or") {
    const out: QualifiedName[] = [];
    for (const t of sel.terms) {
      if (t.kind !== "ref") return undefined;
      out.push(t.path);
    }
    return out;
  }
  return undefined;
}

// =============================================================================
// Resolution
// =============================================================================

interface WorkLane {
  id: string;
  title: string | null;
  memberKeys: string[];
  declIndex: number;
  poolId?: string;
  /** Universe keys of members (actors/unbridged excluded) — pool matching. */
  memberUniverseKeys: (string | undefined)[];
  /** WYSIWYG edit correlation. `clauseKind` picks the editability rule; the
   *  1:1 test (`editable`) is finalized after all clauses expand. */
  clauseKind?: FlowBodyClause["kind"];
  ownerModule?: string;
}

interface WorkPool {
  id: string;
  title: string;
  declIndex: number;
  /** Universe key set the pool query matched; null = `pool by` (value rule). */
  match: ReadonlySet<string> | null;
  byGetter?: ExprGetter;
  byValue?: string;
  /** True for a 1:1 `pool Ref` / `pool "T": sel`; false for `pool by` pools. */
  editable?: boolean;
}

/** Spec §8.1 total order — code-point compare, never `localeCompare` (the
 *  default collator honors LC_ALL: `ch` sorts after `h` under cs_CZ, so the
 *  same model would render different lane orders on different machines). */
function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Evaluate a lane/pool selector to a node set — INERT on failure (house
 *  style): an edge-sort pattern, `this`, an unbound `$knob`, or any other
 *  unevaluable selector yields the empty set plus a `bad-selector` lint
 *  instead of throwing through the render pipeline (a throw here would crash
 *  the live flow tab on every keystroke of a half-typed selector). */
function nodeSet(
  sel: Selector,
  ctx: EvalContext,
  lints: BpmnLaneLint[],
  where: string,
  source: SourceSpan | undefined,
): Set<string> {
  try {
    const r = evalSelector(sel, ctx);
    if (r.sort !== "node") {
      lints.push({
        kind: "bad-selector",
        message: `${where} takes a node-sort selector — an edge pattern selects edges, not performers`,
        ...(source ? { source } : {}),
      });
      return new Set();
    }
    return r.nodes;
  } catch (err) {
    lints.push({
      kind: "bad-selector",
      message: `${where} selector cannot evaluate here — ${err instanceof Error ? err.message : String(err)}`,
      ...(source ? { source } : {}),
    });
    return new Set();
  }
}

/**
 * Evaluate a flow body into the lane/pool plan.
 *
 * `performers` — the rendered walk's performer set ({@link performersOf});
 * `staticParticipantKeys` — the splice-inclusive participation set for the
 * empty-lane lint (a performer hidden inside a collapsed subprocess must not
 * lint); undefined = lint against the rendered set only.
 */
export function resolveBpmnLanes(
  body: readonly FlowBodyClause[],
  performers: readonly BpmnPerformer[],
  ctx: EvalContext,
  staticParticipantKeys?: ReadonlySet<string>,
): BpmnLanePlan {
  const lints: BpmnLaneLint[] = [];

  // Performer → universe bridge (memoized; actors never bridge).
  const uniKey = new Map<string, string | undefined>();
  for (const p of performers) {
    const key = performerKey(p);
    if (!uniKey.has(key)) {
      uniKey.set(key, p.isActor ? undefined : universeNodeFor(ctx, p.path)?.key);
    }
  }
  const bridged = (p: BpmnPerformer): string | undefined => uniKey.get(performerKey(p));

  const claimed = new Set<string>();
  const unclaimed = () => performers.filter((p) => !claimed.has(performerKey(p)));

  const lanes: WorkLane[] = [];
  const pools: WorkPool[] = [];
  const byTitleThenId = (a: { title: string | null; id: string }, b: typeof a) =>
    cmp(a.title ?? "", b.title ?? "") || cmp(a.id, b.id);
  // Value getter dispatch — the group-by precedent (view-projection
  // groupValuesOf): `@@` reads the aspect map, `@` reads the field map.
  const valuesOf = (
    node: ElementNode | undefined,
    getter: ExprGetter,
  ): readonly Value[] | undefined => {
    if (!node) return undefined;
    const dotted = getter.path.join(".");
    if (getter.sigil === "@@") return node.aspects.get(dotted);
    const f = node.fields.get(dotted);
    return f !== undefined ? [f] : undefined;
  };
  // `val:` ids are qualified by the getter path so equal values of two
  // different `lane by` getters never collide.
  const valLaneId = (getter: ExprGetter, canon: string) =>
    `val:${getter.sigil}${getter.path.join(".")}:${canon}`;

  const claimInto = (lane: WorkLane, p: BpmnPerformer) => {
    const key = performerKey(p);
    claimed.add(key);
    lane.memberKeys.push(key);
    lane.memberUniverseKeys.push(bridged(p));
  };
  // Two pool clauses may share a title (different queries) — ids stay unique.
  const usedPoolIds = new Set<string>();
  const uniquePoolId = (base: string): string => {
    let id = base;
    for (let n = 2; usedPoolIds.has(id); n++) id = `${base}#${n}`;
    usedPoolIds.add(id);
    return id;
  };

  body.forEach((clause, declIndex) => {
    switch (clause.kind) {
      case "lane-expand":
      case "lane-merge": {
        const matchSet = nodeSet(clause.selector, ctx, lints, "a lane clause", clause.source);
        const refAtoms = refAtomsOf(clause.selector);
        const matches = unclaimed().filter((p) => {
          const uk = bridged(p);
          if (uk !== undefined && matchSet.has(uk)) return true;
          // Actor-by-name: bare ref / or-union of refs only (§2 shape gate).
          if (p.isActor && refAtoms) {
            return refAtoms.some((r) => r.length === 1 && r[0] === p.path[0]);
          }
          return false;
        });
        if (clause.kind === "lane-merge") {
          // Same title = same lane: a second `lane "X": …` clause merges its
          // claims into the existing lane (ids must stay unique — the
          // post-pass keys bands by id).
          const id = `title:${clause.title}`;
          let lane = lanes.find((l) => l.id === id);
          if (!lane) {
            lane = {
              id,
              title: clause.title,
              memberKeys: [],
              memberUniverseKeys: [],
              declIndex,
              clauseKind: "lane-merge",
            };
            lanes.push(lane);
          }
          for (const p of matches) claimInto(lane, p);
        } else {
          // One lane per distinct element (instances merge), lexicographic.
          const byElement = new Map<string, { performers: BpmnPerformer[]; title: string }>();
          for (const p of matches) {
            const elemKey = p.isActor ? p.path.join(".") : bridged(p) ?? p.path.join(".");
            const g = byElement.get(elemKey) ?? {
              performers: [],
              title: p.path[p.path.length - 1] ?? p.label,
            };
            g.performers.push(p);
            byElement.set(elemKey, g);
          }
          const expanded = [...byElement.entries()]
            .map(([id, g]) => ({ id, ...g }))
            .sort(byTitleThenId);
          for (const e of expanded) {
            const lane: WorkLane = {
              id: e.id,
              title: e.title,
              memberKeys: [],
              memberUniverseKeys: [],
              declIndex,
              clauseKind: "lane-expand",
            };
            for (const p of e.performers) claimInto(lane, p);
            lanes.push(lane);
          }
        }
        // Empty-lane lint — explicit clauses only, static-set aware, never
        // for a ref naming a persona actor (it may perform without being
        // in any universe).
        if (matches.length === 0) {
          const staticHit =
            staticParticipantKeys !== undefined &&
            [...matchSet].some((k) => staticParticipantKeys.has(k));
          const namesActor =
            refAtoms?.some((r) => r.length === 1 && performers.some((p) => p.isActor && p.path[0] === r[0])) ??
            false;
          if (!staticHit && !namesActor) {
            lints.push({
              kind: "empty-lane",
              message:
                "lane matches no participant — lanes hold performers (a module that is only called here performs no action)",
              ...(clause.source ? { source: clause.source } : {}),
            });
          }
        }
        break;
      }
      case "lane-by": {
        const dotted = clause.getter.path.join(".");
        const groups = new Map<string, { title: string; performers: BpmnPerformer[] }>();
        for (const p of unclaimed()) {
          const uk = bridged(p);
          if (uk === undefined) continue; // actors / unbridged: no values
          const values = valuesOf(ctx.universe.byKey(uk), clause.getter);
          if (!values || values.length === 0) continue;
          const canon = values
            .map((v) => ({ canon: canonicalValue(v, ctx), display: displayValue(v) }))
            .sort((a, b) => cmp(a.canon, b.canon));
          if (canon.length > 1) {
            lints.push({
              kind: "multi-value",
              message: `\`${p.label}\` carries ${canon.length} values of ${clause.getter.sigil}${dotted} — placed in the first (\`${canon[0]!.display}\`)`,
              ...(clause.source ? { source: clause.source } : {}),
            });
          }
          const pick = canon[0]!;
          const g = groups.get(pick.canon) ?? { title: pick.display, performers: [] };
          g.performers.push(p);
          groups.set(pick.canon, g);
        }
        const expanded = [...groups.entries()]
          .map(([canon, g]) => ({ id: valLaneId(clause.getter, canon), ...g }))
          .sort(byTitleThenId);
        for (const e of expanded) {
          const lane: WorkLane = {
            id: e.id,
            title: e.title,
            memberKeys: [],
            memberUniverseKeys: [],
            declIndex,
            clauseKind: "lane-by",
          };
          for (const p of e.performers) claimInto(lane, p);
          lanes.push(lane);
        }
        break;
      }
      case "pool-ref": {
        if (clause.ref.kind === "knob") {
          // An element-kind knob is an element atom; desugar through the
          // selector layer so binding resolution stays in one place.
          const knobSel: Selector = { kind: "knob", name: clause.ref.name };
          const inSel: Selector = { kind: "in", arg: knobSel };
          const orSel: Selector = { kind: "or", terms: [knobSel, inSel] };
          const match = nodeSet(orSel, ctx, lints, "a pool clause", clause.source);
          const title = clause.ref.name;
          pools.push({ id: uniquePoolId(`pool:$${title}`), title, declIndex, match, editable: true });
          break;
        }
        const path = clause.ref.path;
        const refSel: Selector = { kind: "ref", path };
        const inSel: Selector = { kind: "in", arg: refSel };
        const orSel: Selector = { kind: "or", terms: [refSel, inSel] };
        pools.push({
          id: uniquePoolId(`pool:${path.join(".")}`),
          title: path[path.length - 1] ?? "",
          declIndex,
          match: nodeSet(orSel, ctx, lints, "a pool clause", clause.source),
          editable: true,
        });
        break;
      }
      case "pool-merge": {
        pools.push({
          id: uniquePoolId(`pool:title:${clause.title}`),
          title: clause.title,
          declIndex,
          match: nodeSet(clause.selector, ctx, lints, "a pool clause", clause.source),
          editable: true,
        });
        break;
      }
      case "pool-by": {
        pools.push({
          id: `pool:by:${clause.getter.path.join(".")}`,
          title: "",
          declIndex,
          match: null,
          byGetter: clause.getter,
        });
        break;
      }
    }
  });

  if (pools.length > 0 && !body.some((c) => c.kind.startsWith("lane"))) {
    lints.push({
      kind: "pools-only",
      message:
        "pool clauses group lanes — declare lanes too (did you mean `lane by` the same getter?)",
    });
  }

  // The implicit trailing default lane.
  const defaultLane: WorkLane = {
    id: DEFAULT_LANE_ID,
    title: null,
    memberKeys: [],
    memberUniverseKeys: [],
    declIndex: body.length,
  };
  for (const p of unclaimed()) claimInto(defaultLane, p);
  lanes.push(defaultLane);

  // ── Pool assignment ────────────────────────────────────────────────────────
  // Expand `pool by` into concrete value pools at its declaration position:
  // a lane joins value-V's pool iff ALL members share V (lex-first shared
  // value when several). Then: first matching pool wins, declaration order.
  const concretePools: WorkPool[] = [];
  for (const pool of pools) {
    if (pool.match !== null) {
      concretePools.push(pool);
      continue;
    }
    const getter = pool.byGetter!;
    const valuePools = new Map<string, WorkPool>();
    for (const lane of lanes) {
      if (lane.memberKeys.length === 0) continue;
      let shared: Map<string, string> | undefined; // canon → display
      for (const uk of lane.memberUniverseKeys) {
        const values =
          uk !== undefined ? valuesOf(ctx.universe.byKey(uk), getter) : undefined;
        const canon = new Map<string, string>(
          (values ?? []).map((v) => [canonicalValue(v, ctx), displayValue(v)]),
        );
        if (shared === undefined) shared = canon;
        else for (const k of [...shared.keys()]) if (!canon.has(k)) shared.delete(k);
        if (shared.size === 0) break;
      }
      if (!shared || shared.size === 0) continue;
      const pick = [...shared.entries()].sort((a, b) => cmp(a[0], b[0]))[0]!;
      const id = valLaneId(getter, pick[0]);
      if (!valuePools.has(id)) {
        valuePools.set(id, {
          id: `pool:${id}`,
          title: pick[1],
          declIndex: pool.declIndex,
          match: null,
          byValue: pick[0],
        });
      }
    }
    concretePools.push(
      ...[...valuePools.values()].sort((a, b) => cmp(a.title, b.title) || cmp(a.id, b.id)),
    );
  }

  const laneMatchesPool = (lane: WorkLane, pool: WorkPool): boolean => {
    if (lane.memberKeys.length === 0) return false;
    if (pool.byValue !== undefined) {
      const getter = pools.find((p) => p.declIndex === pool.declIndex)!.byGetter!;
      return lane.memberUniverseKeys.every((uk) => {
        const values = uk !== undefined ? valuesOf(ctx.universe.byKey(uk), getter) : undefined;
        return (values ?? []).some((v) => canonicalValue(v, ctx) === pool.byValue);
      });
    }
    return lane.memberUniverseKeys.every((uk) => uk !== undefined && pool.match!.has(uk));
  };

  for (const lane of lanes) {
    for (const pool of concretePools) {
      if (laneMatchesPool(lane, pool)) {
        lane.poolId = pool.id;
        break;
      }
    }
  }

  const usedPools = concretePools.filter((p) => lanes.some((l) => l.poolId === p.id));

  // ── Render order: pooled lanes group contiguously at their anchor ─────────
  const laneOrder = new Map(lanes.map((l, i) => [l, i]));
  const poolAnchor = new Map<string, number>();
  for (const l of lanes) {
    if (l.poolId !== undefined && !poolAnchor.has(l.poolId)) {
      poolAnchor.set(l.poolId, laneOrder.get(l)!);
    }
  }
  const ordered = [...lanes].sort((a, b) => {
    const ai = a.poolId !== undefined ? poolAnchor.get(a.poolId)! : laneOrder.get(a)!;
    const bi = b.poolId !== undefined ? poolAnchor.get(b.poolId)! : laneOrder.get(b)!;
    return ai - bi || laneOrder.get(a)! - laneOrder.get(b)!;
  });

  // ── WYSIWYG edit correlation (S-lane-edit) ────────────────────────────────
  // A band edits 1:1 iff its clause produced exactly ONE band (`lane "T": sel`
  // always does; `lane Ref` only when it matched a single element). `by`/value
  // and default bands never do. `ownerModule` = the single module a band
  // projects (its sole bridged member), for `lane`-kind bands only.
  const expandCounts = new Map<number, number>();
  for (const l of lanes) {
    if (l.clauseKind === "lane-expand") {
      expandCounts.set(l.declIndex, (expandCounts.get(l.declIndex) ?? 0) + 1);
    }
  }
  const editableOf = (l: WorkLane): boolean => {
    if (l.clauseKind === "lane-merge") return true;
    if (l.clauseKind === "lane-expand") return (expandCounts.get(l.declIndex) ?? 0) === 1;
    return false;
  };
  const ownerModuleOf = (l: WorkLane): string | undefined => {
    if (l.clauseKind !== "lane-expand" && l.clauseKind !== "lane-merge") return undefined;
    const uks = new Set(l.memberUniverseKeys);
    // Every member must bridge to a module (no actors/unbridged) and share ONE.
    if (uks.size !== 1) return undefined;
    const only = [...uks][0];
    return only === undefined ? undefined : only;
  };

  return {
    lanes: ordered.map((l) => ({
      id: l.id,
      title: l.title,
      memberKeys: l.memberKeys,
      ...(l.poolId !== undefined ? { poolId: l.poolId } : {}),
      ...(l.clauseKind !== undefined && l.clauseKind !== "lane-by"
        ? { sourceClauseIndex: l.declIndex }
        : {}),
      editable: editableOf(l),
      ...(ownerModuleOf(l) !== undefined ? { ownerModule: ownerModuleOf(l) } : {}),
    })),
    pools: usedPools.map((p) => ({
      id: p.id,
      title: p.title,
      ...(p.editable ? { sourceClauseIndex: p.declIndex, editable: true } : {}),
    })),
    lints,
  };
}

// =============================================================================
// Post-pass — remap a laneless layout onto lane bands
// =============================================================================

const isComp = (n: BpmnNode): boolean => "compensation" in n && n.compensation === true;

/** TODO = an ownerless call/await step — fail-closed into the default lane.
 *  Everything else ownerless (terminators, collapsed `do`, prose annotations,
 *  synthesized furniture) inherits from its flow neighborhood instead. */
function isTodoStep(n: BpmnNode): boolean {
  if (n.kind === "task") return n.stepIndex !== null && !n.subprocess;
  if (n.kind === "event") {
    return (
      (n.event === "start" || n.event === "intermediate") &&
      n.stepIndex !== undefined &&
      !n.id.startsWith("#")
    );
  }
  return false;
}

/**
 * `assignBpmnLanes` — the P3 post-pass. `bpmnLayout` stays untouched; this
 * remaps y per lane by order-preserving compaction (x never changes):
 * local track = rank of the node's original y among its lane's distinct
 * original-y values. Compensation bands stay global below all lane bands.
 * Frames re-bbox from geometric membership captured PRE-remap (nodeId-shape
 * rules would re-swallow diverted comp nodes and miss `#dist:N` frames).
 *
 * Returns the input layout object itself when the plan collapses to the
 * laneless case (a single unnamed, unpooled default band) — byte-identity by
 * construction. A single NAMED or POOLED band keeps geometry (identity remap,
 * frames pass through untouched) and emits the band + pool chrome.
 */
export function assignBpmnLanes(layout: BpmnLayout, plan: BpmnLanePlan): BpmnLayout {
  const laneByMember = new Map<string, string>();
  for (const l of plan.lanes) for (const k of l.memberKeys) laneByMember.set(k, l.id);

  const mainNodes = layout.nodes.filter((n) => !isComp(n));
  const compNodes = layout.nodes.filter(isComp);
  const nodeIds = new Set(layout.nodes.map((n) => n.id));

  // ── Node → lane resolution ────────────────────────────────────────────────
  const laneOf = new Map<string, string>();
  const pendingInherit: BpmnNode[] = [];
  for (const n of mainNodes) {
    const owner = "owner" in n ? n.owner : undefined;
    if (owner && owner.path.length > 0) {
      laneOf.set(n.id, laneByMember.get(performerKey(owner)) ?? DEFAULT_LANE_ID);
    } else if (isTodoStep(n)) {
      laneOf.set(n.id, DEFAULT_LANE_ID);
    } else {
      pendingInherit.push(n);
    }
  }

  // Inheritance graph — seq/assoc edges only (`go` re-entries and comp
  // chains are not flow adjacency). First edge in edge order = tie-break.
  const flowEdges = layout.edges.filter((e) => e.style === "seq" || e.style === "assoc");
  const firstIncoming = new Map<string, string[]>();
  const firstOutgoing = new Map<string, string[]>();
  for (const e of flowEdges) {
    (firstIncoming.get(e.to) ?? firstIncoming.set(e.to, []).get(e.to)!).push(e.from);
    (firstOutgoing.get(e.from) ?? firstOutgoing.set(e.from, []).get(e.from)!).push(e.to);
  }
  const wantsSuccessor = (n: BpmnNode): boolean =>
    n.id === "#start" || (n.kind === "event" && n.event === "boundary-error");
  // Fixpoint: neighbors may themselves be unresolved furniture.
  for (let pass = 0; pass < mainNodes.length && pendingInherit.length > 0; pass++) {
    let progressed = false;
    for (let i = pendingInherit.length - 1; i >= 0; i--) {
      const n = pendingInherit[i]!;
      let lane: string | undefined;
      if (n.kind === "gateway" && n.gate === "merge") {
        const splitId = `${n.frameId}#split`;
        lane = laneOf.get(splitId);
        // The split-sibling rule (plan §4): while the split node EXISTS but
        // is itself unresolved, the merge must WAIT for it — falling through
        // to the predecessor rule in the same visit would land the merge in
        // a branch arm's lane (fleet finding 3). Try merges have no split
        // and use the plain predecessor rule.
        if (lane === undefined && nodeIds.has(splitId)) continue;
      }
      if (lane === undefined) {
        const neighbors = wantsSuccessor(n)
          ? firstOutgoing.get(n.id)
          : firstIncoming.get(n.id);
        for (const nb of neighbors ?? []) {
          lane = laneOf.get(nb);
          if (lane !== undefined) break;
        }
      }
      if (lane !== undefined) {
        laneOf.set(n.id, lane);
        pendingInherit.splice(i, 1);
        progressed = true;
      }
    }
    if (!progressed) break;
  }
  // Transitive phase — the strict fixpoint can genuinely deadlock (`#start`
  // wants its successor, a leading ownerless split wants its predecessor —
  // fleet finding 4). Walk each stalled node's preferred direction THROUGH
  // unresolved intermediates to the nearest resolved lane; iterate until
  // stable (resolving one node can unblock another).
  for (let pass = 0; pass < mainNodes.length && pendingInherit.length > 0; pass++) {
    let progressed = false;
    for (let i = pendingInherit.length - 1; i >= 0; i--) {
      const n = pendingInherit[i]!;
      let lane: string | undefined;
      if (n.kind === "gateway" && n.gate === "merge") {
        lane = laneOf.get(`${n.frameId}#split`);
      }
      if (lane === undefined) {
        const dir = wantsSuccessor(n) ? firstOutgoing : firstIncoming;
        const seen = new Set<string>([n.id]);
        const queue = [...(dir.get(n.id) ?? [])];
        while (queue.length > 0 && lane === undefined) {
          const id = queue.shift()!;
          if (seen.has(id)) continue;
          seen.add(id);
          lane = laneOf.get(id);
          if (lane === undefined) queue.push(...(dir.get(id) ?? []));
        }
      }
      if (lane !== undefined) {
        laneOf.set(n.id, lane);
        pendingInherit.splice(i, 1);
        progressed = true;
      }
    }
    if (!progressed) break;
  }
  for (const n of pendingInherit) laneOf.set(n.id, DEFAULT_LANE_ID); // isolated — fail closed

  // ── Band survival ─────────────────────────────────────────────────────────
  // A member-less band survives only if it is DECLARED (the user wrote it): an
  // element-path lane (`lane Ref`), a titled lane (`title:*`), or a pooled
  // lane. `lane "Money": Payments` renders even when Payments is only ever a
  // callee (a `lane` holds performers = step owners; a callee owns no step) —
  // dropping it was the bug. DERIVED bands — `lane by @@getter` value bands
  // (`val:*`) and the synthesized `#default` — are noise when empty and stay
  // suppressed. `laneOf` never assigns a node to a dropped band (an empty band
  // has no nodes), so this only changes WHICH empty bands render, not ownership.
  const usedLaneIds = new Set(laneOf.values());
  const isDerivedBandId = (id: string) => id === DEFAULT_LANE_ID || id.startsWith("val:");
  const bands = plan.lanes.filter((l) => usedLaneIds.has(l.id) || !isDerivedBandId(l.id));
  const lanelessCollapse =
    bands.length <= 1 &&
    (bands.length === 0 ||
      (bands[0]!.id === DEFAULT_LANE_ID &&
        bands[0]!.title === null &&
        bands[0]!.poolId === undefined));
  if (lanelessCollapse) return layout;

  const usedPools: BpmnPoolBand[] = plan.pools.filter((p) =>
    bands.some((b) => b.poolId === p.id),
  );

  // Copy the WYSIWYG edit-correlation fields a plan lane carries onto its band.
  const corr = (b: BpmnLaneDef) => ({
    ...(b.sourceClauseIndex !== undefined ? { sourceClauseIndex: b.sourceClauseIndex } : {}),
    ...(b.editable !== undefined ? { editable: b.editable } : {}),
    ...(b.ownerModule !== undefined ? { ownerModule: b.ownerModule } : {}),
  });

  const mainMaxY = Math.max(...mainNodes.map((n) => n.y), 0);
  if (bands.length === 1) {
    // Identity remap: geometry (nodes AND frames) passes through untouched —
    // re-bboxing would shrink frames whose rect exceeds their member bbox.
    const b = bands[0]!;
    const lanes: BpmnLaneBand[] = [
      { id: b.id, title: b.title, ...(b.poolId ? { poolId: b.poolId } : {}), y0: 0, y1: mainMaxY, ...corr(b) },
    ];
    return { ...layout, lanes, pools: usedPools };
  }

  // ── y-remap: per-lane order-preserving compaction ─────────────────────────
  // Frame membership FIRST (geometric, pre-remap, comp excluded).
  const frameMembers = layout.frames.map((f) =>
    mainNodes.filter((n) => n.x >= f.x0 && n.x <= f.x1 && n.y >= f.y0 && n.y <= f.y1),
  );

  const rankByLane = new Map<string, Map<number, number>>();
  for (const b of bands) {
    const ys = [
      ...new Set(mainNodes.filter((n) => laneOf.get(n.id) === b.id).map((n) => n.y)),
    ].sort((a, z) => a - z);
    rankByLane.set(b.id, new Map(ys.map((y, i) => [y, i])));
  }
  let offset = 0;
  const bandOut: BpmnLaneBand[] = [];
  const offsetByLane = new Map<string, number>();
  for (const b of bands) {
    // A retained member-less DECLARED band occupies one visible row (its
    // rankByLane is empty → size 0); populated bands are unchanged (size ≥ 1).
    const h = Math.max(rankByLane.get(b.id)!.size, 1);
    offsetByLane.set(b.id, offset);
    bandOut.push({
      id: b.id,
      title: b.title,
      ...(b.poolId ? { poolId: b.poolId } : {}),
      y0: offset,
      y1: offset + h - 1,
      ...corr(b),
    });
    offset += h;
  }
  const lanesHeight = offset;

  const newY = new Map<string, number>();
  for (const n of mainNodes) {
    const lane = laneOf.get(n.id)!;
    newY.set(n.id, offsetByLane.get(lane)! + rankByLane.get(lane)!.get(n.y)!);
  }
  // Compensation bands: global, below all lanes, relative geometry kept.
  const compYs = [...new Set(compNodes.map((n) => n.y))].sort((a, z) => a - z);
  const compRank = new Map(compYs.map((y, i) => [y, i]));
  for (const n of compNodes) newY.set(n.id, lanesHeight + compRank.get(n.y)!);

  // Only `y` moves — step-attached props (`danglingEngine`, diff, …) ride the spread.
  const nodes = layout.nodes.map((n) => ({ ...n, y: newY.get(n.id)! }));
  const frames = layout.frames.map((f, i) => {
    const members = frameMembers[i]!;
    if (members.length === 0) return f; // keep the original rect
    const ys = members.map((m) => newY.get(m.id)!);
    return { ...f, y0: Math.min(...ys), y1: Math.max(...ys) };
  });

  return {
    ...layout,
    nodes,
    frames,
    height: lanesHeight + compYs.length,
    lanes: bandOut,
    pools: usedPools,
  };
}
